import { Observable, Subscription } from 'rxjs';

import { Component, OnInit, ViewChild, Inject, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormGroup, FormArray, FormControl, FormBuilder, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { formatDate } from '@angular/common';

import { AppConst } from '../../app-const';
import { Facility } from '../../firestore-models/facility';
import { ProjectLanguage } from '../../firestore-models/project-language';

import { ConfigurationService } from '../../configuration.service';

import { SelectHallsDialogComponent } from '../select-halls-dialog/select-halls-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { DateAdapter, NativeDateAdapter } from '@angular/material/core'; //(0703)

@Component({
  selector: 'app-select-purchase-content-daily-dialog',
  templateUrl: './select-purchase-content-daily-dialog.component.html',
  styleUrls: ['./select-purchase-content-daily-dialog.component.css'],
  standalone: false,
  providers: [
    // このコンポーネントとその子要素専用のDateAdapterインスタンスを作成 //(0703)
    { provide: DateAdapter, useClass: NativeDateAdapter },
  ]
})
export class SelectPurchaseContentDailyDialogComponent implements OnInit, OnDestroy {

  editForm : FormGroup;
  statuses: number[] = AppConst.RESERVATION_STATUSES;

  //各コンテンツ購入日次情報に紐付くプロジェクトID
  projectId: string;
  //各コンテンツ購入日次情報に紐付くプロジェクトの言語情報
  projectLanguages!: Array<ProjectLanguage>;

  private facilitiesCollection!: AngularFirestoreCollection<Facility>;
  facilitiesObservable!: Observable<Facility[]>;
  facilitiesSubscribe!: Subscription;
  facilities!: Array<Facility>;
  private langChangeSub!: Subscription; // TranslateServiceの購読を管理 //(0703)

  projectBaseLang: string = 'ja';
  currentLang: string = 'ja';

  constructor(
    public dialogRef: MatDialogRef<SelectPurchaseContentDailyDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialog: MatDialog,
    private formBuilder: FormBuilder,
    private configurationService: ConfigurationService,
    private angularFirestore: AngularFirestore,
    private translate: TranslateService,
    private dateAdapter: DateAdapter<any>//(0703)
  ) {
    this.projectId = this.configurationService.getProjectId();
    let startDate = null;
    let endDate = null;
    if (!!data.start_date) {
      const year = parseInt(data.start_date.substring(0, 4));
      const month = parseInt(data.start_date.substring(4, 6));
      const day = parseInt(data.start_date.substring(6, 8));
      startDate = new Date(year, month - 1, day, 0, 0);
    }
    if (!!data.end_date) {
      const year = parseInt(data.end_date.substring(0, 4));
      const month = parseInt(data.end_date.substring(4, 6));
      const day = parseInt(data.end_date.substring(6, 8));
      endDate = new Date(year, month - 1, day, 0, 0);
    }
    this.editForm = this.formBuilder.group({
      facility_id : new FormControl({value: data.facility_id, disabled: false }, []),
      hall_id: new FormControl({value: data.hall_id, disabled: false }, []),
      start_date: new FormControl({value: startDate, disabled: false }, []),
      end_date: new FormControl({value: endDate, disabled: false }, []),
      status: new FormControl({value: data.status,  disabled: false }, []),
    });
  }

  ngOnInit(): void {
    this.projectLanguages = this.configurationService.getProject().languages!;

    // 施設取得
    this.facilitiesCollection = this.angularFirestore.collection<Facility>(
      AppConst.FIRESTORE_FACILITIES, ref => ref.where('project_id', '==', this.projectId).orderBy('facility_id', 'asc'));
    this.facilitiesObservable = this.facilitiesCollection.valueChanges({ idField: 'id' });
    this.facilitiesSubscribe = this.facilitiesObservable.subscribe(facilities => {
      this.facilities = facilities;
    });
    this.projectBaseLang = this.configurationService.getProject().project_base_lang;
    this.currentLang = this.translate.currentLang; //(0703)
    // 1. コンポーネント初期化時に現在の言語でカレンダーを設定
    this.setCalendarLocale(this.currentLang);

    // 2. アプリケーションの言語が変更されたら、カレンダーの言語も動的に追従させる
    this.langChangeSub = this.translate.onLangChange.subscribe(event => {
      this.setCalendarLocale(event.lang);
    });
  }

  ngOnDestroy(): void {
    this.facilitiesSubscribe.unsubscribe();
  }

  /** //(0703)
  * Datepickerのロケールを設定するプライベートメソッド
  * @param lang 設定したい言語コード (例: 'en', 'ja')
  */
  private setCalendarLocale(lang: string): void {
    // 言語に応じたロケールを設定
    const locale = lang === 'en' ? 'en-US' : 'ja-JP';
    this.dateAdapter.setLocale(locale);
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  search(): void {
    const startDate       = !!this.start_date.value ? formatDate(this.start_date.value, 'yyyyMMdd', 'en-US') : null;
    const endDate         = !!this.start_date.value ? formatDate(this.end_date.value, 'yyyyMMdd', 'en-US') : null;
    const data = {
      facility_id: this.facility_id.value,
      hall_id: this.hall_id.value,
      start_date: startDate,
      end_date: endDate,
    }
    this.dialogRef.close(data);
  }

  clearDate(): void {
    this.start_date.setValue(null);
    this.end_date.setValue(null);
  }

  //建物・カテゴリ選択ダイアログ
  openSelectHallsDialog(): void {
    const dialogRef = this.dialog.open(SelectHallsDialogComponent, {
      width: '80%',
      height: '90%',
      disableClose: true,
      data: null
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result) {
        this.hall_id.setValue(result.hall_id);
      }
    });
  }

  //================================================================================================
  //  getter
  //================================================================================================
  get facility_id(): AbstractControl { return this.editForm.get('facility_id')!; }
  get hall_id(): AbstractControl { return this.editForm.get('hall_id')!; }
  get start_date(): AbstractControl { return this.editForm.get('start_date')!; }
  get end_date(): AbstractControl { return this.editForm.get('end_date')!; }
}
